import type { ImportTargetField } from '../../functions/import/fuzzyColumnMatch';
import {
    InventoryCategory,
    InventoryImportCreateItemInput,
    InventoryUnit,
} from '../../types/inventory';
import { INVENTORY_CATEGORY_OPTIONS, parseInventoryPartType } from '../../utils/inventoryConstants';
import { dedupeTagNames } from '../../utils/inventoryTags';
import { formatPartNumber } from '../formatPartNumber';

export const INVENTORY_IMPORT_TARGET_FIELDS: ImportTargetField[] = [
    {
        id: 'category',
        label: 'Category',
        description: 'One of: part, material, or liquid.',
        required: true,
    },
    {
        id: 'manufacturerPartNumber',
        label: 'Primary Part Number',
        description:
            'Primary manufacturer part number (alphanumeric). Multiple values may be separated by commas or semicolons; the first becomes primary.',
        required: true,
    },
    {
        id: 'additionalPartNumbers',
        label: 'Additional Part Numbers',
        description:
            'Optional alternate part numbers, separated by commas or semicolons. All are searchable for order matching.',
        required: false,
    },
    {
        id: 'name',
        label: 'Name',
        description: 'Display name of the inventory item.',
        required: true,
    },
    {
        id: 'partType',
        label: 'Part Type',
        description:
            'One of: oem, a/m, rcy, oe-surplus, re-man, re-con, other (or UI labels like OEM).',
        required: true,
    },
    {
        id: 'supplierName',
        label: 'Supplier',
        description: 'Supplier name as shown in Yaxle (matched on the server).',
        required: true,
    },
    {
        id: 'locationTitle',
        label: 'Location',
        description: 'Location title as shown in Yaxle (matched on the server).',
        required: true,
    },
    {
        id: 'unit',
        label: 'Unit',
        description: 'Unit of measure (e.g. each, sheet, gallon).',
        required: true,
    },
    {
        id: 'unitCost',
        label: 'Unit Cost',
        description: 'Required when in-stock quantity is greater than zero; required for mapping.',
        required: true,
    },
    {
        id: 'restockAlert',
        label: 'Restock Alert',
        description:
            'true/false (or yes/no). When false, reorder and target quantities are optional. Defaults to false when omitted.',
        required: false,
    },
    {
        id: 'origin',
        label: 'Origin',
        description:
            'Optional RO number (open or closed in the last 90 days). Set only at import/create; not editable later.',
        required: false,
    },
    {
        id: 'reorderQuantity',
        label: 'Reorder Quantity',
        description:
            'Quantity at which the item is flagged for reorder. Required when Restock Alert is true.',
        required: false,
    },
    {
        id: 'targetQuantity',
        label: 'Target Quantity',
        description: 'Desired on-hand quantity. Required when Restock Alert is true.',
        required: false,
    },
    {
        id: 'initialInStockQuantity',
        label: 'In-Stock Quantity',
        description: 'Starting quantity on hand for the new item.',
        required: true,
    },
    {
        id: 'tagNames',
        label: 'Tags',
        description: 'Optional tags, separated by commas or semicolons.',
        required: false,
    },
    {
        id: 'binNames',
        label: 'Bins',
        description:
            'Optional storage bins (rack/building/box) at the row location, separated by commas or semicolons.',
        required: false,
    },
    {
        id: 'imageUrl',
        label: 'Image',
        description:
            'Optional image URL (e.g. export image_url). Fetched and staged by the server.',
        required: false,
    },
];

/** Targets that union all mapped source columns (joined, then parsed). */
export const INVENTORY_IMPORT_UNION_FIELD_IDS: ReadonlySet<string> = new Set([
    'tagNames',
    'binNames',
    'manufacturerPartNumber',
    'additionalPartNumbers',
]);

const VALID_CATEGORIES = new Set(INVENTORY_CATEGORY_OPTIONS.map((o) => o.value));

function parseNumber(value: string, fallback = 0): number {
    const n = Number(String(value).replace(/[,$]/g, '').trim());
    return Number.isFinite(n) ? n : fallback;
}

function parseOptionalNumber(value: string | undefined): number | null {
    const trimmed = (value ?? '').trim();
    if (!trimmed) return null;
    const n = Number(String(trimmed).replace(/[,$]/g, ''));
    return Number.isFinite(n) ? n : null;
}

function parseBoolean(value: string | undefined, fallback = false): boolean {
    const v = (value ?? '').trim().toLowerCase();
    if (!v) return fallback;
    if (['true', 'yes', 'y', '1'].includes(v)) return true;
    if (['false', 'no', 'n', '0'].includes(v)) return false;
    return fallback;
}

function parseTagNames(value: string): string[] | undefined {
    const tags = dedupeTagNames(
        value
            .split(/[;,]/)
            .map((t) => t.trim())
            .filter(Boolean),
    );
    return tags.length ? tags : undefined;
}

function parseBinNames(value: string): string[] | undefined {
    return parseTagNames(value);
}

/** Split on commas or semicolons only; format each token as a part number. */
function parseDelimitedPartNumbers(value: string): string[] {
    return value
        .split(/[;,]/)
        .map((t) => formatPartNumber(t))
        .filter(Boolean);
}

function dedupePartNumbers(parts: string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const part of parts) {
        if (!part || seen.has(part)) continue;
        seen.add(part);
        result.push(part);
    }
    return result;
}

function normalizeCategory(value: string): InventoryCategory {
    const v = value.trim().toLowerCase();
    if (VALID_CATEGORIES.has(v as InventoryCategory)) return v as InventoryCategory;
    return 'part';
}

function normalizeUnit(value: string): InventoryUnit {
    const v = value.trim() as InventoryUnit;
    return v || 'each';
}

export function mappedRowsToImportItems(
    rows: Array<Record<string, string>>,
): InventoryImportCreateItemInput[] {
    return rows.map((row) => {
        const initialInStockQuantity = parseNumber(row.initialInStockQuantity, 0);
        const unitCostRaw = row.unitCost?.trim();
        const restockAlert = parseBoolean(row.restockAlert, false);
        const reorderQuantity = restockAlert
            ? parseNumber(row.reorderQuantity, 0)
            : parseOptionalNumber(row.reorderQuantity);
        const targetQuantity = restockAlert
            ? parseNumber(row.targetQuantity, 0)
            : parseOptionalNumber(row.targetQuantity);
        const origin = (row.origin ?? '').trim();
        const mfgParts = parseDelimitedPartNumbers(row.manufacturerPartNumber ?? '');
        const additionalParts = parseDelimitedPartNumbers(row.additionalPartNumbers ?? '');
        const primary = mfgParts[0] ?? additionalParts[0] ?? '';
        const additionalPartNumbers = dedupePartNumbers([...mfgParts, ...additionalParts]).filter(
            (pn) => pn !== primary,
        );
        const item: InventoryImportCreateItemInput = {
            manufacturerPartNumber: primary,
            name: (row.name ?? '').trim(),
            category: normalizeCategory(row.category ?? 'part'),
            partType: parseInventoryPartType(row.partType),
            unit: normalizeUnit(row.unit ?? 'each'),
            restockAlert,
            reorderQuantity,
            targetQuantity,
            initialInStockQuantity,
            supplierName: (row.supplierName ?? '').trim(),
            locationTitle: (row.locationTitle ?? '').trim(),
            ...(origin ? { origin } : {}),
        };
        if (initialInStockQuantity > 0 && unitCostRaw) {
            item.unitCost = parseNumber(unitCostRaw, 0);
        }
        if (additionalPartNumbers.length) item.additionalPartNumbers = additionalPartNumbers;
        const tags = parseTagNames(row.tagNames ?? '');
        if (tags) item.tagNames = tags;
        const bins = parseBinNames(row.binNames ?? '');
        if (bins) item.binNames = bins;
        const imageUrl = (row.imageUrl ?? '').trim();
        if (imageUrl) item.imageUrl = imageUrl;
        return item;
    });
}

export { INVENTORY_CATEGORY_OPTIONS };
